import * as d3 from "d3";
import { swapDialog, closeAllDialogs } from './dialog';
function resolve(link) {
    return link;
}
function createTreeSimulation(container, containerSelector, rootNodes) {
    var _a;
    const svg = d3.select(containerSelector);
    const svgEl = (_a = svg.node()) !== null && _a !== void 0 ? _a : (() => {
        throw new Error('mindmap: no svg found for ' + containerSelector);
    })();
    let props;
    function calc() {
        const { width, height } = svgEl.getBoundingClientRect();
        svgEl.setAttribute('viewBox', `0 0 ${width} ${height}`);
        props = {
            width: width,
            height: height,
            center: {
                x: width / 2,
                y: height / 2
            },
            // clamp scale between 0.6 and 1, and only scale lower than 700px wide
            scale: Math.max(0.5, Math.min(width / 700, 1))
        };
    }
    calc();
    window.addEventListener('resize', () => {
        calc();
        updateSimulation();
    });
    const circles = svg.selectAll('g.circles');
    const lines = svg.selectAll('g.lines');
    const simulation = d3.forceSimulation()
        .force("charge", d3.forceManyBody().strength(-2000))
        .force("collide", d3.forceCollide(d => (d.r * props.scale) + 1));
    simulation.force('bounds', forceBounds(30, 5))
        .on("tick", tick);
    // sets initial data props for TreeNode, 
    // and generates link data between parent/child nodes
    function buildVisibleGraph() {
        const nodes = [];
        const links = [];
        function visit(node, parent, level = 0) {
            node.level = level;
            if (node.fixPosition) {
                node.fx = node.fixPosition.x * props.width;
                node.fy = node.fixPosition.y * props.height;
            }
            else {
                node.fx = undefined;
                node.fy = undefined;
            }
            nodes.push(node);
            if (!node.expanded || !node.children)
                return;
            for (const child of node.children) {
                links.push({ source: node.id, target: child.id });
                child.parent = node;
                visit(child, node, level + 1);
            }
        }
        for (const root of rootNodes)
            visit(root);
        return { nodes, links };
    }
    function updateSimulation() {
        calc();
        const { nodes, links } = buildVisibleGraph();
        simulation.nodes(nodes);
        simulation.force("link", d3.forceLink(links)
            .id(d => d.id)
            .distance(d => {
            const { source, target } = resolve(d);
            return (source.r + target.r) * 1.15 * props.scale;
        })
            .strength(2));
        simulation.alpha(0.2).restart();
        render(nodes, links);
    }
    function forceBounds(padding = 20, strength = 0.1) {
        let nodes;
        function force(alpha) {
            var _a, _b;
            for (const d of nodes) {
                if (d.x == null || d.y == null)
                    continue;
                const vx = (_a = d.vx) !== null && _a !== void 0 ? _a : 0;
                const vy = (_b = d.vy) !== null && _b !== void 0 ? _b : 0;
                // left
                if (d.x < padding)
                    d.vx = vx + (padding - d.x) * strength * alpha;
                // right
                if (d.x > props.width - padding)
                    d.vx = vx + (props.width - padding - d.x) * strength * alpha;
                // top
                if (d.y < padding)
                    d.vy = vy + (padding - d.y) * strength * alpha;
                // bottom
                if (d.y > props.height - padding)
                    d.vy = vy + (props.height - padding - d.y) * strength * alpha;
            }
        }
        force.initialize = function (_nodes) {
            nodes = _nodes;
        };
        return force;
    }
    function render(nodes, links) {
        // Nodes
        circles
            .selectAll("g")
            .data(nodes, d => d.id)
            .join(function (enter) {
            const g = enter.append('g');
            g.call(drag(simulation));
            g.on("click", (event, d) => {
                if (toggleNode(d))
                    swapDialog(d.id, container);
                else
                    closeAllDialogs(container);
                event.stopPropagation();
            });
            g.attr('aria-controls', d => `dialog-${d.id}`);
            g.attr('class', d => {
                return [
                    'mindmap-circle',
                    d.children && 'mindmap-circle--has-children',
                    d.className || '',
                    `level-${d.level}`
                ].filter(Boolean).join(' ');
            });
            const circle = g.append("circle")
                .attr("r", d => d.r * props.scale)
                .attr("fill", "#222")
                .attr("stroke", "#fff")
                .attr("stroke-width", 0);
            g.append("foreignObject")
                .attr("width", d => d.r * 2 * props.scale)
                .attr("height", d => d.r * 2 * props.scale)
                .attr("x", d => { var _a; return ((_a = d.x) !== null && _a !== void 0 ? _a : 0) - (d.r * props.scale); })
                .attr("y", d => { var _a; return ((_a = d.y) !== null && _a !== void 0 ? _a : 0) - (d.r * props.scale); })
                .html(d => `<div class="node"><div class="node__inner">${d.text}</div></div>`);
            return g;
        }, update => update, exit => exit.remove());
        // Links
        lines
            .selectAll("line")
            .data(links, d => `${d.source}-${d.target}`)
            .join(enter => enter
            .append("line")
            .attr("stroke", "#fff")
            .attr("stroke-width", 2)
            .attr('class', d => {
            var _a;
            const { source } = resolve(d);
            return `mindmap-line level-${(_a = source.level) !== null && _a !== void 0 ? _a : 0}`;
        }), update => update, exit => exit.remove());
    }
    function tick() {
        const g = circles.selectAll("g");
        const circle = g.selectAll("circle");
        const text = g.selectAll("foreignObject");
        circle
            .attr("cx", d => { var _a; return (_a = d.x) !== null && _a !== void 0 ? _a : 0; })
            .attr("cy", d => { var _a; return (_a = d.y) !== null && _a !== void 0 ? _a : 0; })
            .attr("r", d => d.r * props.scale);
        text
            .attr("width", d => d.r * 2 * props.scale)
            .attr("height", d => d.r * 2 * props.scale)
            .attr("x", d => { var _a; return ((_a = d.x) !== null && _a !== void 0 ? _a : 0) - d.r * props.scale; })
            .attr("y", d => { var _a; return ((_a = d.y) !== null && _a !== void 0 ? _a : 0) - d.r * props.scale; });
        lines.selectAll("line")
            .each(function (d) {
            const { source, target } = d;
            // nodes are placed on the first tick, so guard until then
            const c1 = source;
            const c2 = target;
            if (!(c1 === null || c1 === void 0 ? void 0 : c1.x) || !c1.y || !(c2 === null || c2 === void 0 ? void 0 : c2.x) || !c2.y)
                return;
            const pts = lineBetweenCircles(c1, c2, 5, props.scale);
            d3.select(this)
                .attr("x1", pts.from.x)
                .attr("y1", pts.from.y)
                .attr("x2", pts.to.x)
                .attr("y2", pts.to.y);
        });
    }
    function toggleNode(node) {
        var _a, _b;
        const openIntent = !node.expanded;
        if (openIntent) {
            closeNodesAtLevel(node.level || 0);
            node.expanded = true;
            // assign layout to children
            const children = node.children;
            if (!children)
                return node.expanded;
            const pos = { x: (_a = node.x) !== null && _a !== void 0 ? _a : 0, y: (_b = node.y) !== null && _b !== void 0 ? _b : 0 };
            const childPos = generateChildPositions(node, pos, node.level || 0);
            for (let i = 0; i < childPos.length; i++) {
                const pos = childPos[i];
                children[i].x = pos.x;
                children[i].y = pos.y;
            }
        }
        else {
            if (node.level === 0)
                return node.expanded;
            node.expanded = false;
        }
        updateSimulation();
        return node.expanded;
    }
    function closeNodesAtLevel(level) {
        function visit(node) {
            if (node.level === level) {
                node.expanded = false;
            }
            if (!node.children)
                return;
            for (const child of node.children)
                visit(child);
        }
        for (const root of rootNodes)
            visit(root);
    }
    function drag(simulation) {
        function dragstarted(event, d) {
            if (!event.active)
                simulation.alphaTarget(0.3).restart();
            if (d.fixPosition)
                return;
            d.fx = d.x;
            d.fy = d.y;
        }
        function dragged(event, d) {
            if (d.fixPosition)
                return;
            d.fx = event.x;
            d.fy = event.y;
        }
        function dragended(event, d) {
            if (!event.active)
                simulation.alphaTarget(0);
            if (d.fixPosition)
                return;
            d.fx = undefined;
            d.fy = undefined;
        }
        return d3.drag()
            .on("start", dragstarted)
            .on("drag", dragged)
            .on("end", dragended);
    }
    function init(node) {
        // assign layout to children of root
        if (!node.children)
            return;
        const childPos = generateChildPositions(node, props.center, 0);
        for (let i = 0; i < childPos.length; i++) {
            const pos = childPos[i];
            node.children[i].x = pos.x;
            node.children[i].y = pos.y;
        }
    }
    // NOTE: dialog.ts also closes on click, so no need for additional handler
    rootNodes.map(init);
    return { updateSimulation };
}
// helpers
function generateChildPositions(node, pos, level = 0) {
    var _a;
    const radius = node.r * 1.2 / Math.max(1, level);
    const count = ((_a = node.children) === null || _a === void 0 ? void 0 : _a.length) || 0;
    const startAngleR = Math.PI / count;
    if (level === 0) {
        return generateCirclePoints({ center: pos, count, startAngleR, radius });
    }
    else {
        const parent = {
            x: node.parent.x,
            y: node.parent.y,
        };
        const direction = Math.atan2(parent.y - node.y, parent.x - node.x) + Math.PI;
        return generateFanPointsByRadius({ center: pos, direction, radius, count, itemSize: 150 });
    }
}
function generateFanPointsByRadius({ center, direction, radius, count, itemSize }) {
    if (count <= 0)
        return [];
    const points = [];
    if (count === 1) {
        // single point: place it directly at the target direction
        points.push({
            x: Math.round(center.x + Math.cos(direction) * radius),
            y: Math.round(center.y + Math.sin(direction) * radius)
        });
        return points;
    }
    // angular spacing per item so items don’t overlap
    const anglePerItem = 2 * Math.atan(itemSize / (2 * radius));
    const totalArc = anglePerItem * (count - 1);
    const startAngle = direction - totalArc / 2;
    for (let i = 0; i < count; i++) {
        const angle = startAngle + i * anglePerItem;
        points.push({
            x: Math.round(center.x + Math.cos(angle) * radius),
            y: Math.round(center.y + Math.sin(angle) * radius)
        });
    }
    return points;
}
function generateCirclePoints({ center, radius, count, startAngleR = 0 }) {
    const points = [];
    const step = (Math.PI * 2) / count;
    for (let i = 0; i < count; i++) {
        const angle = startAngleR + step * i;
        points.push({
            x: Math.round(center.x + Math.cos(angle) * radius),
            y: Math.round(center.y + Math.sin(angle) * radius)
        });
    }
    return points;
}
function lineBetweenCircles(c1, c2, gap = 5, scaleRadius = 1) {
    const dx = c2.x - c1.x;
    const dy = c2.y - c1.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance === 0) {
        // circles overlap exactly, just return their centers
        return { from: Object.assign({}, c1), to: Object.assign({}, c2) };
    }
    const ux = dx / distance;
    const uy = dy / distance;
    return {
        from: {
            x: c1.x + ux * (c1.r * scaleRadius + gap),
            y: c1.y + uy * (c1.r * scaleRadius + gap)
        },
        to: {
            x: c2.x - ux * (c2.r * scaleRadius + gap),
            y: c2.y - uy * (c2.r * scaleRadius + gap)
        }
    };
}
// example
// const tree: TreeNode = {
//     id: "root",
//     text: 'Our values',
//     r: 150,
//     fixPosition: { 
//     	x: 0.5, 
//     	y: 0.5 
//     },
//     expanded: true,
//     className: 'root',
//     children: [
//         {
//             id: "a",
//             text: 'Empowering people',
//             r: 70,
//             children: [
//                 { id: "a1", text: 'Lorem ipsum dolor est 1', r: 50 },
//                 { id: "a2", text: 'Lorem ipsum dolor est 2', r: 50 },
//                 { id: "a3", text: 'Lorem ipsum dolor est 3', r: 50 },
//                 { id: "a4", text: 'Lorem ipsum dolor est 4', r: 50 }
//             ]
//         },
//         {
//             id: "b",
//             text: 'Maintaining trust',
//             r: 70,
//             children: [
//                { id: "b1", text: 'Lorem ipsum dolor est 1', r: 50 },
//                { id: "b2", text: 'Lorem ipsum dolor est 2', r: 50 },
//                { id: "b3", text: 'Lorem ipsum dolor est 3', r: 50 },
//                { id: "b4", text: 'Lorem ipsum dolor est 4', r: 50 }
//            ]
//         },
//         {
//             id: "c",
//             text: 'Protect the planet',
//             r: 70,
//             children: [
//                { id: "c1", text: 'Lorem ipsum dolor est 1', r: 50 },
//                { id: "c2", text: 'Lorem ipsum dolor est 2', r: 50 },
//                { id: "c3", text: 'Lorem ipsum dolor est 3', r: 50 },
//                { id: "c4", text: 'Lorem ipsum dolor est 4', r: 50 }
//            ]
//         },
//         {
//             id: "d",
//             text: 'Innovation',
//             r: 70,
//             children: [
//                { id: "d1", text: 'Lorem ipsum dolor est 1', r: 50 },
//                { id: "d2", text: 'Lorem ipsum dolor est 2', r: 50 },
//                { id: "d3", text: 'Lorem ipsum dolor est 3', r: 50 },
//                { id: "d4", text: 'Lorem ipsum dolor est 4', r: 50 }
//            ]
//         }
//     ]
// }
window.addEventListener('elementor/frontend/init', () => {
    // @ts-expect-error
    elementorFrontend.hooks.addAction('frontend/element_ready/mindmap.default', $scope => {
        var _a;
        const container = $scope[0] || $scope;
        const el = container.querySelector('.mindmap');
        const treeData = JSON.parse(((_a = el.querySelector('.mindmap__script')) === null || _a === void 0 ? void 0 : _a.textContent) || '{}');
        const sim = createTreeSimulation(el, ".mindmap-svg", [treeData]);
        sim.updateSimulation();
    });
});
